import { Form, PasswordInput, required, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';

const minLength16 = (value: string) =>
  value && value.length >= 16 ? undefined : 'Password must be at least 16 characters';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  isLoading: boolean;
  requireOldPassword: boolean;
}

function ChangePasswordContent({ onClose, onSubmit, isLoading, requireOldPassword }: Omit<ChangePasswordDialogProps, 'open'>) {
  const t = useTranslate();
  const { handleSubmit } = useFormContext();

  return (
    <>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {requireOldPassword && (
            <PasswordInput
              source="oldPassword"
              label={t('module.user.oldPassword')}
              fullWidth
              validate={required()}
              autoComplete="current-password"
            />
          )}
          <PasswordInput
            source="newPassword"
            label={t('module.user.newPassword')}
            fullWidth
            validate={[required(), minLength16]}
            autoComplete="new-password"
          />
          <PasswordInput
            source="confirmPassword"
            label={t('module.user.confirmPassword')}
            fullWidth
            validate={[required(), minLength16]}
            autoComplete="new-password"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('module.user.buttons.cancel')}</Button>
        <Button
          variant="contained"
          disabled={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {isLoading ? 'Updating...' : t('module.user.buttons.update')}
        </Button>
      </DialogActions>
    </>
  );
}

export function ChangePasswordDialog({
  open,
  onClose,
  onSubmit,
  isLoading,
  requireOldPassword,
}: ChangePasswordDialogProps) {
  const t = useTranslate();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('module.user.changePassword')}</DialogTitle>
      <Form onSubmit={onSubmit}>
        <ChangePasswordContent onClose={onClose} onSubmit={onSubmit} isLoading={isLoading} requireOldPassword={requireOldPassword} />
      </Form>
    </Dialog>
  );
}